import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '../../../../../../../colors';
import { formatNumber } from '../../../../../../../helpers/NumberFormatter';

import './index.css';

import { darkenColor } from '../../../../../../../helpers/colors';
import {
  DomainTuple,
  Point,
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import { MrcStateDataPoint } from '../CandleView';
import moment from 'moment';
import {
  createXTickValues,
  findClosestCandleTimestamp,
  getFormatUnit,
} from '../../../../../../../helpers/bundleTransactionsToCompactForm';
import MultiChart, {
  XAxisInfo,
} from '../../../../../../../components/MultiChart';
import {
  Band,
  IndicatorSingleValue,
  ProfitResultIndicatorConfigs,
  ZoningBand,
} from '../../../../../../ProfitCalculations/types';
import { ComponentDimensions } from '../../../../../../../types/Layout';
import {
  createYAxisCandlesticksTicks,
  formatToCandlestickDataPointType,
  formatYTick,
} from '../../../../../../../helpers/Chart';
import { LatestBotState } from '../../../../../../Detail/types';
import TransactionItemStructure from '../../../../../../../components/TransactionItem';
import RSIChart from '../../../../../../../components/Charts/Rsi';

interface Props {
  latestBotState: LatestBotState;
  complexParamsChangeTimestamps: number[] | undefined;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

interface ChartProps {
  latestBotState: LatestBotState;

  height?: number | undefined;
  width?: number | undefined;
  setXAxisInfo?: React.Dispatch<React.SetStateAction<XAxisInfo | undefined>>;
  movingAverages?: Array<{
    data: IndicatorSingleValue[];
    color: string;
    label: string;
  }>;
  bollingerBand?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
  keltnerChannel?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
}

const Chart = (props: ChartProps) => {
  const { latestBotState } = props;

  const { height, width, setXAxisInfo, bollingerBand, keltnerChannel } = props;

  const { candles, cbrtRangeStates, mrcStates, transactions } = latestBotState;

  const candleDataPoints = useMemo(() => {
    if (!latestBotState.candles?.length) {
      return [];
    }

    const dataPoints = latestBotState.candles.map((c) =>
      formatToCandlestickDataPointType(c),
    );

    return dataPoints;
  }, [latestBotState.candles]);

  const cbrtLines = useMemo(() => {
    if (!cbrtRangeStates?.length || !candleDataPoints?.length) {
      return [];
    }

    const filteredCbrtRangeStates: Array<
      {
        x: number;
        y: number;
        color: string;
        label?: string;
      }[]
    > = [];

    cbrtRangeStates.forEach((cbrtRangeState, index) => {
      const isLast = index === cbrtRangeStates.length - 1;

      const color =
        cbrtRangeState.direction === 'up' ? colors.green : colors.red;
      const close =
        cbrtRangeState.open +
        cbrtRangeState.cbrt *
          cbrtRangeState.open *
          (cbrtRangeState.direction === 'up' ? 1 : -1);

      const negativeClose = cbrtRangeState.open - (close - cbrtRangeState.open);

      /** Break positive side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: close,
          color: isLast ? color : darkenColor(color, 20),
          label: `cbrt: ${formatNumber(close, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: close,
          color: isLast ? color : darkenColor(color, 20),
        },
      ]);

      const negativeColor =
        cbrtRangeState.direction === 'up'
          ? `${colors.red}66`
          : `${colors.green}66`;

      /** Break negative side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: negativeClose,
          color: negativeColor,
          label: `cbrt: ${formatNumber(negativeClose, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: negativeClose,
          color: negativeColor,
        },
      ]);
    });

    return filteredCbrtRangeStates;
  }, [candleDataPoints?.length, cbrtRangeStates]);

  const mrcStatesLines = useMemo(() => {
    if (!mrcStates?.length || !candleDataPoints?.length) {
      return [];
    }

    /** For each item, make a line with a start and end */
    const items: Array<Array<MrcStateDataPoint>> = [];

    mrcStates.forEach((mrcState) => {
      if (mrcState.startTimestamp === mrcState.endTimestamp) {
        return;
      }

      const baseColor =
        mrcState.orderType === 'BUY' ? colors.green : colors.red;
      const color =
        mrcState.state === 'INITIALISED'
          ? darkenColor(baseColor, 10)
          : mrcState.state === 'PENDING'
          ? darkenColor(baseColor, 50)
          : baseColor;

      const start = mrcState.startTimestamp;
      const end = mrcState.endTimestamp || new Date().valueOf();

      items.push([
        {
          x: start,
          y: mrcState.startPrice,
          color,
          date: new Date(start),
          label: `mrc: ${mrcState.state}`,
          orderType: mrcState.orderType,
        },
        {
          x: end,
          y: mrcState.startPrice,
          color,
          date: new Date(end),
        },
      ]);
    });

    return items;
  }, [candleDataPoints, mrcStates]);

  const domain = useMemo(() => {
    if (!candleDataPoints?.length) {
      return undefined;
    }

    const minX = Math.min(...candleDataPoints.map((c) => c.x));

    const minY = Math.min(
      ...candleDataPoints.map((c) => c.low),
      ...cbrtLines.map((ms) => Math.min(...ms.map((s) => s.y))),
      ...mrcStatesLines.map((ms) => Math.min(...ms.map((s) => s.y))),
    );

    const maxY = Math.max(
      ...candleDataPoints.map((c) => c.high),
      ...cbrtLines.map((ms) => Math.max(...ms.map((s) => s.y))),
      ...mrcStatesLines.map((ms) => Math.max(...ms.map((s) => s.y))),
    );

    /** If a cbrt range of mrc state started too close to the end
     * The text is cut off.
     * We want to add a little more the to xDomain to text is visible
     *
     * Give a specific length to cbrt range and mrc state in terms of amount of candles
     * If the latest one started between the end and minus the period,
     * we want to add a bit more range based on the period - (end - start of cbrt range)
     */

    let extraRange = 0;
    const cbrtRangeAmountOfCandles = 4;
    const mrcStateAmountOfCandles = 23;
    const now = new Date().valueOf();
    const candleInterval = candleDataPoints[0].x2 - candleDataPoints[0].x;
    const startOfCbrtPeriod = now - cbrtRangeAmountOfCandles * candleInterval;
    const startOfPeriod = now - mrcStateAmountOfCandles * candleInterval;

    /** Check cbrt range first because it's the shortest */
    const currentCbrtRange = cbrtRangeStates.find((state) => {
      const startOfState = state.timestamp;

      if (startOfCbrtPeriod <= startOfState) {
        return true;
      }
    });

    if (currentCbrtRange) {
      /** set extraRange */
      const periodRange = now - startOfCbrtPeriod;
      const statePeriodRange = now - currentCbrtRange.timestamp;
      const periodDifference = Math.abs(periodRange - statePeriodRange);

      extraRange = periodDifference;
    }

    const currentMrcState = mrcStates.find((state) => {
      const startOfState = state.startTimestamp;

      if (startOfPeriod <= startOfState) {
        return true;
      }
    });

    if (currentMrcState) {
      /** set extraRange */
      const periodRange = now - startOfPeriod;
      const statePeriodRange = now - currentMrcState.startTimestamp;
      const periodDifference = Math.abs(periodRange - statePeriodRange);

      extraRange = periodDifference;
    }

    return {
      x: [
        minX,
        candleDataPoints[candleDataPoints.length - 1].x + extraRange,
      ] as DomainTuple,
      y: [minY * 0.98, maxY * 1.02] as DomainTuple,
    };
  }, [candleDataPoints, cbrtLines, cbrtRangeStates, mrcStates, mrcStatesLines]);

  const domainPadding = useMemo(() => {
    /** This state sometimes only shows a few candles
     * The fewer the candles, the closer they must be together, to higher the domainPadding
     * Use a minimum of 50
     */

    const amountOfCandles = candles.length;
    return { x: Math.min(50, Math.max(10, amountOfCandles / (1 / 10))) };
  }, [candles.length]);

  const yTickValues = useMemo(() => {
    if (!domain) {
      return [];
    }

    return createYAxisCandlesticksTicks(
      candleDataPoints,
      transactions,
      bollingerBand?.data || keltnerChannel?.data,
      domain,
    );
  }, [
    bollingerBand?.data,
    candleDataPoints,
    domain,
    keltnerChannel?.data,
    transactions,
  ]);

  const paddingRight = useMemo(() => {
    if (!yTickValues?.length) {
      return 0;
    }

    const longestTick = yTickValues
      .sort((a, b) => b.toString().length - a.toString().length)[0]
      .toString();

    const length = longestTick.length;
    const hasDecimal = longestTick.includes(',') || longestTick.includes('.');
    return 45 + length * 10 - (hasDecimal ? 5 : 0);
  }, [yTickValues]);

  const chartPadding = useMemo(
    () => ({
      left: 0,
      right: paddingRight,
      bottom: setXAxisInfo ? 0 : 30,
      top: 0,
    }),
    [paddingRight, setXAxisInfo],
  );

  const transactionDataPoints = useMemo(() => {
    return transactions.map((t) => {
      return {
        x: findClosestCandleTimestamp(t.timestamp || 0, candles),
        y: t.orderPrice,
        color: t.orderType === 'BUY' ? colors.green : colors.red,
        label: '',
        strokeColor: t.transactionType.includes('STOP_LOSS')
          ? colors.blue
          : t.transactionType.includes('TAKE_PROFIT')
          ? colors.yellow
          : colors.white,
        size: t.transactionType.includes('STOP_LOSS')
          ? 4
          : t.transactionType.includes('TAKE_PROFIT')
          ? 4
          : 6,
      };
    });
  }, [candles, transactions]);

  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!candleDataPoints?.length) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        candleDataPoints[0].x,
        candleDataPoints[candleDataPoints.length - 1].x,
      );

      return moment(t).format(formatUnit);
    },
    [candleDataPoints],
  );

  const tickValues = useMemo(() => {
    if (!candleDataPoints.length) {
      return [];
    }

    return createXTickValues(
      candleDataPoints[0].x,
      candleDataPoints[candleDataPoints.length - 1].x,
    );
  }, [candleDataPoints]);

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: setXAxisInfo ? {} : { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: 'gray' },
    }),
    [setXAxisInfo],
  );

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 20, padding: 5, fill: '#999' },
    }),
    [],
  );

  const getCandleColor = useCallback(
    (c: any) => {
      if (!c.datum) {
        return '#FFA500';
      }

      return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
    },

    [],
  );

  useEffect(() => {
    if (!domain?.x || !tickValues?.length) {
      return;
    }

    setXAxisInfo?.({
      tickValues,
      axisStyle,
      tickFormat: calculateTickValue,
      domain: { x: domain.x as [number, number] },
      domainPadding,
      chartPadding,
    });
  }, [
    axisStyle,
    calculateTickValue,
    chartPadding,
    domain?.x,
    domainPadding,
    setXAxisInfo,
    tickValues,
  ]);

  const chartWidth = width;
  const chartHeight = height;

  return (
    <div
      className={'candlesticks-container'}
      style={{ width: chartWidth, height: chartHeight }}
    >
      <VictoryChart
        domainPadding={domainPadding}
        padding={chartPadding}
        domain={domain}
        width={chartWidth}
        height={chartHeight}
      >
        <VictoryAxis
          tickFormat={!setXAxisInfo ? calculateTickValue : () => ''}
          style={axisStyle}
          tickValues={tickValues}
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={formatYTick}
          orientation={'right'}
          tickValues={yTickValues}
          tickLabelComponent={<VictoryLabel dx={5} />}
        />

        <VictoryCandlestick
          data={candleDataPoints}
          candleColors={{ positive: colors.green, negative: colors.red }}
          style={{
            data: {
              stroke: getCandleColor,
              strokeWidth: 0.5,
              fill: getCandleColor,
            },
          }}
        />

        {cbrtLines.map((line, index) => (
          <VictoryLine
            key={`cbrt_line_${index}`}
            data={line}
            padding={10}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                textAnchor={'start'}
                dy={-5}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 4, bottom: 0, left: 2, right: 6 }}
              />
            }
          />
        ))}

        {mrcStatesLines.map((line, index) => (
          <VictoryLine
            key={`mrc_line_${index}`}
            data={line}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                renderInPortal
                textAnchor={'start'}
                dy={line[0].orderType === 'SELL' ? 9 : -3}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 2, left: 2, right: 2 }}
              />
            }
          />
        ))}

        <VictoryScatter
          data={transactionDataPoints}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: (d) => d.datum.strokeColor,
              strokeWidth: 2,
            },
          }}
          dataComponent={
            <Point className={'single-profit-calcs-chart-scatter-dot'} />
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={3}
              orientation={'left'}
              centerOffset={{ x: -10, y: -40 }}
              pointerOrientation="right"
              pointerWidth={20}
              flyoutStyle={{
                stroke: (d) => d.datum.color,
                strokeWidth: 1.5,
                fill: 'rgba(255, 255, 255, 0.8)',
              }}
              flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
              labelComponent={
                <VictoryLabel
                  textAnchor="start"
                  dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                  style={[
                    {
                      fill: 'rgb(0, 0, 0)',
                      fontFamily: 'monospace',
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  ]}
                />
              }
            />
          }
          size={({ datum }) => datum.size}
        />
      </VictoryChart>
    </div>
  );
};

const LatestBotStateComponent: React.FC<Props> = (props: Props) => {
  const { latestBotState } = props;

  const {
    orderType,
    currentProfit,
    currentPriceChange,
    indicatorConfig,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
  } = latestBotState;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const transactionOrderColor = useMemo(
    () => (orderType === 'BUY' ? colors.green : colors.red),
    [orderType],
  );

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  return (
    <TransactionItemStructure
      circleLabel={''}
      circleColor={transactionOrderColor}
      date={moment().format('D MMM YYYY HH:mm')}
      currentProfit={currentProfit}
      currentPriceChange={currentPriceChange}
      transactionType={orderType}
      content={
        <div
          style={{
            display: 'flex',
            padding: 8,
            height: '70vh',
            width: '100%',
          }}
          ref={rootRef}
        >
          {!!rootDimensions && (
            <MultiChart dimensions={rootDimensions}>
              <Chart latestBotState={latestBotState} />
              {rsiIndicatorValuesOppositeOrder && !!indicatorConfig && (
                <RSIChart
                  key={'Opposite Order Relative Strength Index'}
                  label={`Opposite Order Relative Strength Index (p:${indicatorConfig.oppositeOrderPeriod})`}
                  rsi={rsiIndicatorValuesOppositeOrder}
                  overExtensionThreshold={
                    indicatorConfig.oppositeOrderThreshold
                  }
                  showXAxis={false}
                />
              )}
              {rsiIndicatorValuesSameOrder && !!indicatorConfig && (
                <RSIChart
                  key={'Same Order Relative Strength Index'}
                  label={`Same Order Relative Strength Index (p:${indicatorConfig.sameOrderPeriod})`}
                  rsi={rsiIndicatorValuesSameOrder}
                  overExtensionThreshold={indicatorConfig.sameOrderThreshold}
                  showXAxis={false}
                />
              )}
            </MultiChart>
          )}
        </div>
      }
    />
  );
};

export default LatestBotStateComponent;
